import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useShiprocket } from '../../context/ShiprocketContext';
import Cookies from 'js-cookie';
import { decrpt } from '../../component/encryption';

export default function Address() {
  const { shiprocket } = useShiprocket();

  const [loading, setLoading] = useState(false);
  const [fetchingAddresses, setFetchingAddresses] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    pickup_location: '',
    name: '',
    email: '',
    phone: '',
    house_no: '',
    address: '',
    address_2: '',
    city: '',
    state: '',
    country: 'India',
    pin_code: ''
  });

  // ── Fetch existing pickup addresses ──────────────────────────────────
  useEffect(() => {
    const fetchAddresses = async () => {
      setFetchingAddresses(true);
      try {
        console.log('the fun is running');
        let token = decrpt(Cookies.get('s_l'));

        console.log('the token is', token);

        const res = await axios.get('/seller/shiprocket/pickup-address/list', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        console.log('the res is', res.data);
        if (res.data?.status && Array.isArray(res.data.data)) {
          setAddresses(res.data.data);
        }
      } catch {
        // silently fail; addresses will just be empty
      } finally {
        setFetchingAddresses(false);
      }
    };

    fetchAddresses();
  }, []);

  const handleEdit = (addr) => {
    // Basic attempt to split house_no and address if they were combined with comma
    let houseNo = '';
    let addrStr = addr.address || '';
    if (addrStr.includes(',')) {
      const parts = addrStr.split(',');
      houseNo = parts[0].trim();
      addrStr = parts.slice(1).join(',').trim();
    }
    
    setForm({
      pickup_location: addr.pickup_code || '',
      name: addr.name || '',
      email: addr.email || '',
      phone: addr.phone || '',
      house_no: houseNo,
      address: addrStr,
      address_2: addr.address_2 || '',
      city: addr.city || '',
      state: addr.state || '',
      country: addr.country || 'India',
      pin_code: addr.pin_code || ''
    });
    setEditId(addr.id);
    setShowForm(true);
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const requiredFields = [
      'pickup_location', 'name', 'email', 'phone', 
      'house_no', 'address', 'address_2', 'city', 
      'state', 'country', 'pin_code'
    ];
    
    for (const field of requiredFields) {
      if (!form[field] || form[field].toString().trim() === '') {
        alert('All fields are mandatory. Please fill in all the details.');
        setError('All fields are mandatory. Please fill in all the details.');
        setLoading(false);
        return;
      }
    }

    try {
      // Make pickup location unique for Shiprocket
      const cleanName = form.pickup_location.replace(/[^A-Za-z0-9\-]/g, '').substring(0, 15);
      const uniqueSuffix = `_${Math.floor(Date.now() / 1000)}`;
      
      const payload = {
        pickup_location: cleanName + uniqueSuffix,
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: `${form.house_no}, ${form.address}`,
        address_2: form.address_2,
        city: form.city,
        state: form.state,
        country: form.country,
        pin_code: form.pin_code
      };

      // ── Step 1: Shiprocket ──────────────────────────────────────────
      let srData;
      let token = decrpt(Cookies.get('s_l'));
      try {
        const shiprocketRes = await axios.post(
          '/seller/shiprocket/pickup-address/add',
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Shiprocket-Token': shiprocket.token,
              'Content-Type': 'application/json'
            }
          }
        );
        srData = shiprocketRes.data;
      } catch (srErr) {
        if (axios.isAxiosError(srErr)) {
          if (!srErr.response) {
            throw new Error(
              '📡 Could not reach the server. Please check your internet connection and try again.'
            );
          }

          const status = srErr.response.status;
          const msg = srErr.response.data?.message || srErr.response.data?.error;

          if (status === 401) {
            throw new Error(
              '🔑 Shiprocket authentication failed. Your session may have expired. Please reconnect Shiprocket.'
            );
          } else if (status === 422) {
            const errors = srErr.response.data?.errors;
            if (errors && typeof errors === 'object') {
              const fieldErrors = Object.entries(errors)
                .map(
                  ([field, messages]) =>
                    `• ${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
                )
                .join('\n');
              throw new Error(`❌ Shiprocket rejected the address:\n${fieldErrors}`);
            }
            throw new Error(
              `❌ Shiprocket validation error: ${msg || 'Please check your inputs.'}`
            );
          } else if (status === 409 || (msg && msg.toLowerCase().includes('already exist'))) {
            throw new Error(
              '⚠️ A pickup location with this name already exists on Shiprocket. Please use a different Pickup Location name.'
            );
          } else if (status >= 500) {
            throw new Error(
              '🔧 Shiprocket is currently experiencing issues. Please try again in a few minutes.'
            );
          } else {
            throw new Error(`❌ Shiprocket error: ${msg || `Unexpected error (${status}).`}`);
          }
        }
        throw new Error('❌ An unexpected error occurred while contacting Shiprocket.');
      }

      // ── Step 2: Save to Laravel backend ────────────────────────────
      let savedAddress;
      try {
        const saveRes = await axios.post(
          '/seller/shiprocket/pickup-address/save',
          {
            pickup_id: srData.pickup_id,
            company_id: srData.address.company_id,
            pickup_code: srData.address.pickup_code,
            name: srData.address.name,
            email: srData.address.email,
            phone: srData.address.phone,
            alternate_phone: srData.address.alternate_phone,
            address: srData.address.address,
            address_2: srData.address.address_2,
            city: srData.address.city,
            state: srData.address.state,
            country: srData.address.country,
            pin_code: srData.address.pin_code,
            gstin: srData.address.gstin,
            lat: srData.address.lat,
            long: srData.address.long,
            status: srData.address.status,
            phone_verified: srData.address.phone_verified,
            rto_address_id: srData.address.rto_address_id,
            extra_info: srData.address.extra_info
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        savedAddress = saveRes.data?.data;
      } catch (saveErr) {
        if (axios.isAxiosError(saveErr)) {
          if (!saveErr.response) {
            throw new Error(
              '⚠️ Pickup address was created on Shiprocket but could not be saved to our system due to a network issue. Please contact support with Pickup ID: ' +
                srData.pickup_id
            );
          }

          const status = saveErr.response.status;
          const msg = saveErr.response.data?.message;

          if (status === 401) {
            throw new Error('🔑 Your session has expired. Please log in again and retry.');
          } else if (status === 422) {
            const errors = saveErr.response.data?.errors;
            if (errors && typeof errors === 'object') {
              const fieldErrors = Object.entries(errors)
                .map(
                  ([field, messages]) =>
                    `• ${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
                )
                .join('\n');
              throw new Error(`❌ Could not save address to our system:\n${fieldErrors}`);
            }
            throw new Error(`❌ Validation failed on our system: ${msg || 'Please try again.'}`);
          } else if (status === 409) {
            throw new Error(
              '⚠️ This pickup address is already saved in our system (Pickup ID: ' +
                srData.pickup_id +
                ').'
            );
          } else if (status >= 500) {
            throw new Error(
              '🔧 Our server encountered an error while saving. The address was created on Shiprocket (ID: ' +
                srData.pickup_id +
                '). Please contact support.'
            );
          } else {
            throw new Error(`❌ Failed to save to our system: ${msg || `Error ${status}.`}`);
          }
        }
        throw new Error(
          '❌ Pickup address was created on Shiprocket but an unexpected error prevented saving. Pickup ID: ' +
            srData.pickup_id
        );
      }

      // ── Success: update local list & close form ─────────────────────
      if (savedAddress) {
        setAddresses((prev) => [...prev, savedAddress]);
      }
      setSuccess('✅ Pickup address added and saved successfully!');
      setShowForm(false);
      setForm({
        pickup_location: '',
        name: '',
        email: '',
        phone: '',
        house_no: '',
        address: '',
        address_2: '',
        city: '',
        state: '',
        country: 'India',
        pin_code: ''
      });
    } catch (err) {
      setError(err.message || '❌ Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Address card ────────────────────────────────────────────────────
  const AddressCard = ({ addr }) => (
    <div className="card mb-4 border rounded-3 shadow-sm" style={{ transition: 'all 0.2s', backgroundColor: '#fcfcfc' }}>
      <div className="card-header bg-white border-bottom-0 pt-3 pb-0 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <div>
            <h6 className="mb-0 fw-bold" style={{ color: '#2c3e50' }}>{addr.name}</h6>
            <span className="text-muted small">Code: <strong>{addr.pickup_code}</strong></span>
          </div>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <span className={`badge rounded-pill ${addr.status === 1 ? 'bg-success' : 'bg-secondary'}`} style={{ padding: '0.4em 0.8em' }}>
            {addr.status === 1 ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      <div className="card-body">
        <div className="row g-3">
          <div className="col-md-7">
            <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Location Details</h6>
            <p className="mb-1" style={{ fontSize: '14px', color: '#34495e' }}>
              <i className="bi bi-geo-alt me-2 text-primary"></i>
              {addr.address}{addr.address_2 ? `, ${addr.address_2}` : ''}
            </p>
            <p className="mb-1 ms-4" style={{ fontSize: '14px', color: '#34495e' }}>
              {addr.city}, {addr.state} — <strong>{addr.pin_code}</strong>
            </p>
            <p className="mb-0 ms-4" style={{ fontSize: '14px', color: '#34495e' }}>
              {addr.country}
            </p>
          </div>
          
          <div className="col-md-5 border-start">
            <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Contact & Info</h6>
            <p className="mb-1" style={{ fontSize: '13px', color: '#34495e' }}>
              <i className="bi bi-envelope me-2 text-primary"></i>
              {addr.email}
            </p>
            <p className="mb-1" style={{ fontSize: '13px', color: '#34495e' }}>
              <i className="bi bi-telephone me-2 text-primary"></i>
              {addr.phone}
              {addr.phone_verified ? (
                <i className="bi bi-check-circle-fill text-success ms-2" title="Phone verified"></i>
              ) : (
                <i className="bi bi-exclamation-circle text-warning ms-2" title="Phone unverified"></i>
              )}
            </p>
            {addr.gstin && (
              <p className="mb-0" style={{ fontSize: '13px', color: '#34495e' }}>
                <i className="bi bi-file-earmark-text me-2 text-primary"></i>
                GSTIN: <strong>{addr.gstin}</strong>
              </p>
            )}
          </div>
        </div>
      </div>
      
      <div className="card-footer bg-light border-top text-muted d-flex gap-4" style={{ fontSize: '12px' }}>
        <span>
          <i className="bi bi-box-seam me-1"></i> Pickup ID: <strong>{addr.pickup_id}</strong>
        </span>
        {addr.rto_address_id && (
          <span>
            <i className="bi bi-arrow-return-left me-1"></i> RTO ID: <strong>{addr.rto_address_id}</strong>
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="container mt-4" style={{ maxWidth: 860 }}>
      {/* ── Saved addresses ─────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0 fw-semibold">Pickup Addresses</h5>

        {/* Show "Add" button so sellers can add multiple addresses */}
        {!fetchingAddresses && (
          <button className="btn btn-primary btn-sm rounded-pill px-3 shadow-sm" onClick={() => {
            setEditId(null);
            setForm({
              pickup_location: '', name: '', email: '', phone: '', house_no: '', address: '', address_2: '', city: '', state: '', country: 'India', pin_code: ''
            });
            setShowForm((p) => !p);
          }}>
            <i className={`bi ${showForm ? 'bi-x-lg' : 'bi-plus-lg'} me-1`}></i>
            {showForm ? 'Cancel' : 'Add New Address'}
          </button>
        )}
      </div>

      {success && <div className="alert alert-success">{success}</div>}

      {fetchingAddresses ? (
        <div className="text-center py-5 text-muted">
          <div className="spinner-border spinner-border-sm me-2" role="status" />
          Loading addresses...
        </div>
      ) : addresses.length > 0 ? (
        addresses.map((addr) => <AddressCard key={addr.id} addr={addr} />)
      ) : (
        !showForm && (
          <div className="text-center py-5 text-muted border rounded-3">
            <i className="bi bi-geo-alt fs-2 d-block mb-2 opacity-40"></i>
            No pickup address added yet. Click <strong>Add Pickup Address</strong> to get started.
          </div>
        )
      )}

      {/* ── Collapsible form ────── */}
      {showForm && (
        <div className="card shadow-sm border-0 mt-3">
          <div className="card-header bg-white border-bottom">
            <h6 className="mb-0 fw-semibold">New Pickup Address</h6>
          </div>

          <div className="card-body">
            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Pickup Location <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="pickup_location"
                    placeholder="e.g. Warehouse 1"
                    value={form.pickup_location}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Email <span className="text-danger">*</span></label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Phone <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    House / Floor No. <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="house_no"
                    placeholder="e.g. House No. 12, 3rd Floor"
                    value={form.house_no}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-8 mb-3">
                  <label className="form-label">
                    Street / Area <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="address"
                    placeholder="e.g. MG Road, Beltola"
                    value={form.address}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Address 2 <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="address_2"
                    value={form.address_2}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">City <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">State <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="state"
                    value={form.state}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Pin Code <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="pin_code"
                    value={form.pin_code}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Country <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="d-flex gap-2">
                <button className="btn btn-primary" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      {editId ? 'Updating...' : 'Adding...'}
                    </>
                  ) : (
                    editId ? 'Update Pickup Address' : 'Save Pickup Address'
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowForm(false);
                    setError('');
                    setEditId(null);
                  }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
